import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Container, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { normalizeEmailInput, russianEmailError } from '../utils/email';

const errorText = (error) => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) return String(detail[0]?.msg || 'Некорректные данные').replace(/^Value error, /, '');
  return detail || 'Не удалось выполнить запрос. Попробуйте ещё раз.';
};

const LoginPage = () => {
  const { user, authStep, pendingEmail, requestCode, login, resetAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { if (user) navigate('/dashboard', { replace: true }); }, [user, navigate]);

  const submitEmail = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    const normalizedEmail = normalizeEmailInput(email);
    const blocked = russianEmailError(normalizedEmail);
    if (blocked) { setError(blocked); setBusy(false); return; }
    try { await requestCode(normalizedEmail); setEmail(normalizedEmail); toast.success('Если почта привязана, код отправлен'); }
    catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };

  const submitCode = async (event) => {
    event.preventDefault(); setBusy(true); setError('');
    try { await login(code); navigate('/dashboard', { replace: true }); }
    catch (err) { setError(errorText(err)); }
    finally { setBusy(false); }
  };

  return (
    <Box className="noise-bg" minHeight="100vh" display="grid" sx={{ placeItems: 'center' }} py={5}>
      <Container maxWidth="xs">
        <Paper sx={{ p: { xs: 3, sm: 4 }, bgcolor: 'rgba(16,23,25,.88)', backdropFilter: 'blur(18px)' }}>
          <Box component="img" src="/nox-logo.png" alt="NOX VPN" className="brand-logo" sx={{ mb: 4 }} />
          <Typography variant="h4" mb={1}>{authStep === 'email' ? 'Вход в NOX' : 'Проверьте почту'}</Typography>
          <Typography color="text.secondary" mb={4}>
            {authStep === 'email' ? 'Введите подтверждённую почту. Адреса в доменной зоне .ru не поддерживаются.' : `Мы отправили шестизначный код на ${pendingEmail}. Если аккаунт связан с Telegram, копия кода пришла и в бот.`}
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {authStep === 'email' ? (
            <Box component="form" onSubmit={submitEmail}>
              <Stack spacing={2}>
                <TextField autoFocus required type="email" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" helperText="Российские почтовые сервисы и домены .ru/.su/.рф не принимаются" />
                <Button disabled={busy} type="submit" variant="contained" endIcon={<ArrowForward />}>{busy ? 'Отправляем…' : 'Получить код'}</Button>
              </Stack>
            </Box>
          ) : (
            <Box component="form" onSubmit={submitCode}>
              <Stack spacing={2}>
                <TextField autoFocus required label="Код подтверждения" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric', pattern: '[0-9]{6}', maxLength: 6 }} />
                <Button disabled={busy || code.length !== 6} type="submit" variant="contained">{busy ? 'Проверяем…' : 'Войти'}</Button>
                <Button startIcon={<ArrowBack />} onClick={() => { resetAuth(); setError(''); }}>Изменить почту</Button>
              </Stack>
            </Box>
          )}
          <Alert severity="info" sx={{ mt: 4 }}>Почта ещё не привязана? Откройте Telegram-бота и отправьте команду <b>/email</b>.</Alert>
          <Link component={RouterLink} to="/" underline="hover" display="block" textAlign="center" mt={3}>Вернуться на главную</Link>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
